import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, FolderPlus, Search, Globe2, Lock } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

import { api } from "@/lib/api";
import { FolderIcon, folderColorClasses } from "@/components/folderTheme";
import CreateFolderModal from "@/components/CreateFolderModal";

function fmtRel(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function FoldersListPage() {
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const foldersQuery = useQuery({
    queryKey: ["folders-list", tab, search],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("visibility", tab);
      params.set("page_size", "200");
      if (search.trim()) params.set("search", search.trim());
      return (await api.get(`/folders?${params.toString()}`)).data;
    },
  });

  const folders = useMemo(() => foldersQuery.data?.items || [], [foldersQuery.data]);

  return (
    <div className="space-y-6" data-testid="folders-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Folders
          </h1>
          <p className="text-sm text-slate-500">
            Group demo runs into collections — for pitch decks, training,
            weekly reviews. Folders can be private (just you) or org-wide
            (any eyvo.com user).
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="gap-1.5 bg-indigo-600 hover:bg-indigo-700"
          data-testid="folders-new-button"
        >
          <FolderPlus size={14} /> New folder
        </Button>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="folders-tabs">
            <TabsTrigger value="all" data-testid="folders-tab-all">All</TabsTrigger>
            <TabsTrigger value="mine" data-testid="folders-tab-mine">Mine</TabsTrigger>
            <TabsTrigger value="org" data-testid="folders-tab-org">Org-wide</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1 min-w-[220px] max-w-[360px]">
          <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search folders…"
            className="h-9 pl-8 text-xs"
            data-testid="folders-search"
          />
        </div>
      </div>

      {foldersQuery.isLoading ? (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 className="mr-2 animate-spin" /> Loading folders…
        </div>
      ) : folders.length === 0 ? (
        <EmptyState onCreate={() => setCreateOpen(true)} />
      ) : (
        <div
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          data-testid="folders-grid"
        >
          {folders.map((f) => (
            <FolderCard key={f.folder_id} folder={f} />
          ))}
        </div>
      )}

      <CreateFolderModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => foldersQuery.refetch()}
      />
    </div>
  );
}

function FolderCard({ folder }) {
  const p = folderColorClasses(folder.color);
  return (
    <Link
      to={`/folders/${folder.folder_id}`}
      className={[
        "group relative flex flex-col gap-2 overflow-hidden rounded-xl border bg-white p-4 transition",
        "border-slate-200 hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow-md",
      ].join(" ")}
      data-testid={`folder-card-${folder.folder_id}`}
    >
      {/* Left-border accent in the folder's colour */}
      <span className={["absolute left-0 top-0 h-full w-1", p.accent].join(" ")} aria-hidden />
      <div className="flex items-start gap-3 pl-2">
        <div
          className={["grid h-10 w-10 shrink-0 place-items-center rounded-lg", p.bg, p.text].join(" ")}
        >
          <FolderIcon name={folder.icon} size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="truncate text-sm font-semibold text-slate-900" data-testid={`folder-card-name-${folder.folder_id}`}>
            {folder.name}
          </h3>
          {folder.description && (
            <p className="line-clamp-2 text-[11px] text-slate-500">{folder.description}</p>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between pl-2 text-[11px] text-slate-500">
        <span data-testid={`folder-card-count-${folder.folder_id}`}>
          {folder.item_count} run{folder.item_count === 1 ? "" : "s"}
        </span>
        <div className="flex items-center gap-2">
          {folder.visibility === "org" ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700" data-testid={`folder-card-org-${folder.folder_id}`}>
              <Globe2 size={9} /> Org
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600" data-testid={`folder-card-private-${folder.folder_id}`}>
              <Lock size={9} /> Private
            </span>
          )}
          <span>{fmtRel(folder.updated_at)}</span>
        </div>
      </div>
    </Link>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div
      className="flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-slate-200 bg-white p-12 text-center"
      data-testid="folders-empty"
    >
      <div className="grid h-12 w-12 place-items-center rounded-full bg-indigo-50 text-indigo-600">
        <FolderPlus size={20} />
      </div>
      <h2 className="text-base font-semibold text-slate-900">No folders yet</h2>
      <p className="max-w-md text-sm text-slate-500">
        Create your first folder to organise demo runs. Private folders are
        just for you; org folders are visible to everyone at eyvo.com.
      </p>
      <Button onClick={onCreate} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700">
        <FolderPlus size={14} /> Create folder
      </Button>
    </div>
  );
}
